import { useEffect, useMemo, useRef } from 'react';
import { useApplicationContext } from '../contexts/ApplicationContext';
import { useMutation, useQuery } from '@apollo/client';
import { GetRentalApplicationIncomeDocsDocument, SetDocumentRedactionDocument } from '../graphql/queries';

const MAX_REFETCH_ATTEMPTS = 10;
// Less than 1 second is forbiden by the API spec
const REFETCH_INTERVAL = 6000;

/**
 * Keeps polling redaction related fields for all income documents and returns the doc array
 * Feel free to call this function from multiple components at the same time, because it is cached, it won't refetch unnecessarily
 */
export function useIncomeDocsRedaction() {
  // The refetch counter resets on any unmount/remount
  const refetchCounter = useRef(MAX_REFETCH_ATTEMPTS);
  const applicationId = useApplicationContext().application.id;
  const { data } = useQuery(GetRentalApplicationIncomeDocsDocument, {
    pollInterval: REFETCH_INTERVAL,
    onCompleted: () => {
      refetchCounter.current--;
    },
    skipPollAttempt: () => refetchCounter.current <= 0,
    skip: !applicationId,
    variables: {
      applicationId: applicationId!,
    },
  });

  const docIds = data?.rentalApplication?.answers?.currentIncomes
    ?.flatMap((x) => x?.documents)
    .map((x) => x?.id)
    .join(',');
  useEffect(() => {
    // Reset the refetch counter when set of document ids changes, this detects any new document
    // addition including when they happen on separate devices
    refetchCounter.current = MAX_REFETCH_ATTEMPTS;
  }, [docIds]);

  const [setDocumentRedaction] = useMutation(SetDocumentRedactionDocument, {
    refetchQueries: [GetRentalApplicationIncomeDocsDocument],
  });

  const allDocs = useMemo(() => data?.rentalApplication?.answers?.currentIncomes?.flatMap((x) => x?.documents), [data]);

  const allDocsDoneProcessing = allDocs?.map((x) => x?.isRedactionCheckComplete).every((x) => x === true);

  useEffect(() => {
    if (allDocsDoneProcessing) {
      // stop polling early
      refetchCounter.current = 0;
    }
  }, [allDocsDoneProcessing]);

  useEffect(() => {
    const documentsToRedact = allDocs?.filter((x) => x?.isRedactionCheckComplete && x.canBeRedacted && !x.isRedacted);
    const idToRedact = documentsToRedact?.[0]?.id;
    if (!idToRedact) {
      return;
    }

    // We redact one document at a time, with each refetch
    // we'll move to the next document to redact since the previous one
    // will have isRedacted set to true

    // TODO: Update RAG to support redaction of multiple docs and return
    // cache-friendly data on the mutation
    setDocumentRedaction({
      variables: {
        documentId: idToRedact,
        isRedacted: true,
      },
    });
  }, [allDocs]);
  return allDocs;
}
